import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

export interface NewTicket {
  id: number;
  title: string;
  ticketNumber: string;
  status: number | null;
  priority: number | null;
  queueID: number | null;
  assignedResourceID: number | null;
  companyID: number | null;
}

interface NotificationContextValue {
  newTickets: NewTicket[];
  clearNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextValue>({
  newTickets: [],
  clearNotifications: () => {},
});

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [newTickets, setNewTickets] = useState<NewTicket[]>([]);
  const esRef = useRef<EventSource | null>(null);

  const connect = useCallback(() => {
    const es = new EventSource('/api/notifications/stream');
    esRef.current = es;

    es.addEventListener('new-ticket', (e: MessageEvent) => {
      try {
        const ticket = JSON.parse(String(e.data)) as NewTicket;
        setNewTickets((prev) => {
          if (prev.some((t) => t.id === ticket.id)) return prev;
          return [ticket, ...prev].slice(0, 20);
        });
      } catch {
        // ignore malformed events
      }
    });

    es.onerror = () => {
      es.close();
      esRef.current = null;
      setTimeout(connect, 5_000);
    };
  }, []);

  useEffect(() => {
    connect();
    return () => {
      esRef.current?.close();
    };
  }, [connect]);

  return (
    <NotificationContext.Provider value={{ newTickets, clearNotifications: () => setNewTickets([]) }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}
